import { url } from '../client_config/config';

class NotificationSSEService {
  constructor() {
    this.eventSource = null;
    this.isConnected = false;
    this.notificationListeners = [];
    this.eventListeners = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
  }

  /**
   * Connect to SSE endpoint
   * @param {string} token - JWT authentication token
   */
  connect(token) {
    if (this.eventSource) {
      console.log('SSE already connected or connecting');
      return;
    }

    try {
      // Remove trailing slash and construct proper URL
      let baseUrl = url.replace('/api', '').replace(/\/$/, '');
      const streamUrl = `${baseUrl}/api/notifications/stream?token=${token}`;

      console.log('Connecting to SSE:', streamUrl);
      this.eventSource = new EventSource(streamUrl);

      this.eventSource.addEventListener('open', () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        console.log('SSE notification service connected');
        this.notifyEventListeners({ type: 'connected' });
      });

      this.eventSource.addEventListener('message', (event) => {
        console.log('[SSE] Received message event:', event.data);
        try {
          const notification = JSON.parse(event.data);
          console.log('[SSE] Parsed notification:', notification);
          if (notification.type !== 'connected') {
            console.log('[SSE] Notifying listeners for notification:', notification.title || notification.type);
            this.notifyNotificationListeners(notification);
          } else {
            console.log('[SSE] Ignoring connected type message');
          }
        } catch (error) {
          console.error('[SSE] Error parsing SSE notification:', error, event.data);
        }
      });

      this.eventSource.addEventListener('error', (error) => {
        this.isConnected = false;
        console.error('SSE connection error:', error);
        this.notifyEventListeners({ type: 'error', error });
        this.handleReconnect(token);
      });

    } catch (error) {
      console.error('Failed to connect to SSE:', error);
      this.notifyEventListeners({ type: 'error', error });
    }
  }

  /**
   * Handle reconnection with exponential backoff
   * @param {string} token - JWT token
   */
  handleReconnect(token) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max SSE reconnection attempts reached');
      this.notifyEventListeners({ type: 'max_reconnect_reached' });
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      30000
    );

    console.log(`Reconnecting SSE in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      if (!this.isConnected) {
        this.connect(token);
      }
    }, delay);
  }

  /**
   * Disconnect from SSE endpoint
   */
  disconnect() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.isConnected = false;
      this.reconnectAttempts = 0;
      console.log('SSE notification service disconnected');
      this.notifyEventListeners({ type: 'disconnected' });
    }
  }

  /**
   * Register notification listener
   * @param {Function} callback - Notification callback function
   */
  onNotification(callback) {
    this.notificationListeners.push(callback);
  }

  /**
   * Remove notification listener
   * @param {Function} callback - Notification callback function
   */
  offNotification(callback) {
    this.notificationListeners = this.notificationListeners.filter(
      listener => listener !== callback
    );
  }

  /**
   * Register event listener
   * @param {Function} callback - Event callback function
   */
  onEvent(callback) {
    this.eventListeners.push(callback);
  }

  /**
   * Remove event listener
   * @param {Function} callback - Event callback function
   */
  offEvent(callback) {
    this.eventListeners = this.eventListeners.filter(
      listener => listener !== callback
    );
  }

  /**
   * Notify all notification listeners
   * @param {Object} notification - Notification object
   */
  notifyNotificationListeners(notification) {
    this.notificationListeners.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  /**
   * Notify all event listeners
   * @param {Object} event - Event object
   */
  notifyEventListeners(event) {
    this.eventListeners.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in event listener:', error);
      }
    });
  }

  /**
   * Get connection status
   * @returns {boolean} Connection status
   */
  getConnectionStatus() {
    return this.isConnected;
  }
}

// Create singleton instance
const notificationSSEService = new NotificationSSEService();

export default notificationSSEService;
